import apiClient from '../../shared/services/apiClient';

/**
 * DNS Service (User Version)
 *
 * Handles user-level DNS operations including full CRUD for zones and records.
 *
 * All routes use /api/user/dns/* prefix
 */
class DNSService {
  // ==========================================
  // Zone Management (Full CRUD)
  // ==========================================

  /**
   * Get all DNS zones for the current user
   * @param {object} params - Optional parameters (search, sortField, sortOrder)
   */
  async getDnsZones(params = {}) {
    try {
      const response = await apiClient.get('/api/user/dns/zones', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get single DNS zone details
   * @param {number} zoneId - Zone ID
   */
  async getDnsZone(zoneId) {
    try {
      const response = await apiClient.get(`/api/user/dns/zones/get?id=${zoneId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create a new DNS zone
   * @param {object} zoneData - Zone data (zone, description, etc.)
   */
  async createDnsZone(zoneData) {
    try {
      const response = await apiClient.post('/api/user/dns/zones/create', zoneData);
      return response.data;
    } catch (error) {
      // 保留结构化字段(如 code=ownership_required + verify)
      return { ...(error.response?.data || {}), success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Pre-check a new zone (name / reserved / overlap / plan zone limit) before creating a subscription
   * @param {object} params - { zone, product_id? }
   */
  async precheckDnsZone(params) {
    try {
      const response = await apiClient.post('/api/user/dns/zones/precheck', params);
      return response.data;
    } catch (error) {
      // 保留结构化字段(如 code=ownership_required + verify)
      return { ...(error.response?.data || {}), success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update DNS zone
   * @param {object} zoneData - Zone data including id
   */
  async updateDnsZone(zoneData) {
    try {
      const response = await apiClient.post('/api/user/dns/zones/update', zoneData);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete DNS zone
   * @param {number} zoneId - Zone ID
   */
  async deleteDnsZone(zoneId) {
    try {
      const response = await apiClient.delete('/api/user/dns/zones/delete', { data: { id: zoneId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Record Management (Full CRUD)
  // ==========================================

  /**
   * Get DNS records for a zone
   * @param {object} params - Query parameters (zone_id, type, etc.)
   */
  async getDnsRecords(params) {
    try {
      const response = await apiClient.get('/api/user/dns/records', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create a new DNS record
   * @param {object} recordData - Record data (zone_id, type, name, value, etc.)
   */
  async createDnsRecord(recordData) {
    try {
      const response = await apiClient.post('/api/user/dns/records/create', recordData);
      return response.data;
    } catch (error) {
      return { ...(error.response?.data || {}), success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update DNS record
   * @param {object} recordData - Record data including id
   */
  async updateDnsRecord(recordData) {
    try {
      const response = await apiClient.post('/api/user/dns/records/update', recordData);
      return response.data;
    } catch (error) {
      return { ...(error.response?.data || {}), success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete DNS record
   * @param {number} recordId - Record ID
   */
  async deleteDnsRecord(recordId) {
    try {
      const response = await apiClient.delete('/api/user/dns/records/delete', { data: { id: recordId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Bulk import DNS records
   * @param {object} data - Import data with zone_id and records
   */
  async bulkImportRecords(data) {
    try {
      const response = await apiClient.post('/api/user/dns/records/bulk-import', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // CIDR Management (Full CRUD)
  // ==========================================

  /**
   * Get CIDR groups
   * @param {string} search - Optional search term
   */
  async getCidrs(search = '') {
    try {
      const response = await apiClient.get('/api/user/dns/cidrs', {
        params: { search }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create CIDR group
   * @param {object} cidrData - CIDR group data
   */
  async createCidr(cidrData) {
    try {
      const response = await apiClient.post('/api/user/dns/cidrs/create', cidrData);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update CIDR group
   * @param {object} cidrData - CIDR group data including id
   */
  async updateCidr(cidrData) {
    try {
      const response = await apiClient.post('/api/user/dns/cidrs/update', cidrData);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete CIDR group
   * @param {number} cidrId - CIDR group ID
   */
  async deleteCidr(cidrId) {
    try {
      const response = await apiClient.delete('/api/user/dns/cidrs/delete', { data: { id: cidrId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message, records: error.response?.data?.records };
    }
  }

  /**
   * Get CIDR entries for a group
   * @param {number} cidrId - CIDR group ID
   * @param {string} search - Optional search term
   */
  async getCidrEntries(cidrId, search = '') {
    try {
      const response = await apiClient.get('/api/user/dns/cidr-entries', {
        params: { cidr_id: cidrId, search }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create CIDR entry
   * @param {object} entryData - CIDR entry data
   */
  async createCidrEntry(entryData) {
    try {
      const response = await apiClient.post('/api/user/dns/cidr-entries/create', entryData);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update CIDR entry
   * @param {object} entryData - CIDR entry data including id
   */
  async updateCidrEntry(entryData) {
    try {
      const response = await apiClient.post('/api/user/dns/cidr-entries/update', entryData);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete CIDR entry
   * @param {number} entryId - CIDR entry ID
   */
  async deleteCidrEntry(entryId) {
    try {
      const response = await apiClient.delete('/api/user/dns/cidr-entries/delete', { data: { id: entryId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get CIDR statistics
   */
  async getCidrStats() {
    try {
      const response = await apiClient.get('/api/user/dns/cidr_stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get CIDR information by IDs
   * @param {array} cidrIds - Array of CIDR IDs
   */
  async getCidrInfo(cidrIds) {
    try {
      const response = await apiClient.get(`/api/user/dns/cidr_info?ids=${cidrIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Geographic Information (Read-Only)
  // ==========================================

  /**
   * Get all continents
   */
  async getContinents() {
    try {
      const response = await apiClient.get('/api/user/dns/geo/continents');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get countries by continent
   * @param {number} continentId - Continent ID
   */
  async getCountriesByContinent(continentId) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/countries?continent_id=${continentId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get regions by country
   * @param {string} countryId - Country code
   */
  async getRegions(countryId) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/regions?country_code=${countryId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get cities by country and region
   * @param {string} countryId - Country code
   * @param {number} regionId - Region ID
   */
  async getCities(countryId, regionId) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/cities?country_code=${countryId}&region_id=${regionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get continent information by IDs
   * @param {array} continentIds - Array of continent IDs
   */
  async getContinentInfo(continentIds) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/info/continents?ids=${continentIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get country information by IDs
   * @param {array} countryIds - Array of country codes
   */
  async getCountryInfo(countryIds) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/info/countries?ids=${countryIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get province information by IDs
   * @param {array} provinceIds - Array of province IDs
   */
  async getProvinceInfo(provinceIds) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/info/provinces?ids=${provinceIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get city information by IDs
   * @param {array} cityIds - Array of city IDs
   */
  async getCityInfo(cityIds) {
    try {
      const response = await apiClient.get(`/api/user/dns/geo/info/cities?ids=${cityIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // ISP Information (Read-Only)
  // ==========================================

  /**
   * Get countries with ISP data
   */
  async getIspCountries() {
    try {
      const response = await apiClient.get('/api/user/dns/isp/countries');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get ISP types for a country
   * @param {string} country - Country code
   */
  async getIspTypes(country) {
    try {
      const response = await apiClient.get(`/api/user/dns/isp/types?country=${encodeURIComponent(country)}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get ISP providers
   * @param {string} country - Country code
   * @param {string} type - Optional ISP type
   * @param {string} search - Optional search term
   */
  async getIsps(country, type = null, search = null) {
    let url = `/api/user/dns/isp/providers?country=${encodeURIComponent(country)}`;

    if (type) {
      url += `&usage_type=${encodeURIComponent(type)}`;
    }

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get ISP information by IDs
   * @param {array} ispIds - Array of ISP IDs
   */
  async getIspInfo(ispIds) {
    try {
      const response = await apiClient.get(`/api/user/dns/isp/info?ids=${ispIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Statistics (Read-Only)
  // ==========================================

  /**
   * Get DNS statistics
   */
  async getDnsStats() {
    try {
      const response = await apiClient.get('/api/user/dns/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // DNSSEC Management
  // ==========================================

  /**
   * Get DNSSEC status for a zone
   * @param {number} zoneId - Zone ID
   */
  async getDnssecStatus(zoneId) {
    try {
      const response = await apiClient.get(`/api/user/dns/dnssec/status?zone_id=${zoneId}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Enable DNSSEC for a zone
   * @param {number} zoneId - Zone ID
   * @param {string} algorithm - Algorithm (default: ECDSAP256SHA256)
   */
  async enableDnssec(zoneId, algorithm = 'ECDSAP256SHA256') {
    try {
      const response = await apiClient.post('/api/user/dns/dnssec/enable', {
        zone_id: zoneId,
        algorithm
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Disable DNSSEC for a zone
   * @param {number} zoneId - Zone ID
   */
  async disableDnssec(zoneId) {
    try {
      const response = await apiClient.post('/api/user/dns/dnssec/disable', {
        zone_id: zoneId
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Regenerate DNSSEC keys for a zone
   * @param {number} zoneId - Zone ID
   * @param {string} algorithm - Algorithm (default: ECDSAP256SHA256)
   */
  async regenerateDnssec(zoneId, algorithm = 'ECDSAP256SHA256') {
    try {
      const response = await apiClient.post('/api/user/dns/dnssec/regenerate', {
        zone_id: zoneId,
        algorithm
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * DNSSEC safe key rollover: action = start_zsk|start_ksk|confirm_ds|cancel|auto_ds|dismiss
   */
  async dnssecRollover(zoneId, action) {
    try {
      const response = await apiClient.post('/api/user/dns/dnssec/rollover', { zone_id: zoneId, action });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Products & Nodes
  // ==========================================

  /**
   * Get available DNS products/plans
   */
  async getDnsProducts() {
    try {
      const response = await apiClient.get('/api/user/dns/products');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Check NS status for a zone (manual trigger)
   * @param {number} zoneId - Zone ID
   */
  async checkNsStatus(zoneId) {
    try {
      const response = await apiClient.post('/api/user/dns/zones/check-ns', {
        zone_id: zoneId
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Analytics (Read-Only)
  // ==========================================

  /**
   * Get DNS query analytics
   * @param {object} params - Query parameters (start_time, end_time, zone)
   */
  async getAnalytics(params = {}) {
    try {
      const response = await apiClient.get('/api/user/dns/analytics', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get analytics for a specific zone
   * @param {number} zoneId - Zone ID
   * @param {object} params - Query parameters (start_time, end_time)
   */
  async getZoneAnalytics(zoneId, params = {}) {
    try {
      const response = await apiClient.get(`/api/user/dns/analytics/zone/${zoneId}`, { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get top queried domain names
   * @param {object} params - Query parameters (start_time, end_time, limit, zone)
   */
  async getTopQnames(params = {}) {
    try {
      const response = await apiClient.get('/api/user/dns/analytics/top-qnames', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get query type distribution
   * @param {object} params - Query parameters (start_time, end_time, zone)
   */
  async getQtypeDistribution(params = {}) {
    try {
      const response = await apiClient.get('/api/user/dns/analytics/qtype-distribution', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get geographic distribution
   * @param {object} params - Query parameters (start_time, end_time, zone)
   */
  async getGeoDistribution(params = {}) {
    try {
      const response = await apiClient.get('/api/user/dns/analytics/geo-distribution', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get query trend data
   * @param {object} params - Query parameters (start_time, end_time, interval, zone)
   */
  async getQueryTrend(params = {}) {
    try {
      const response = await apiClient.get('/api/user/dns/analytics/query-trend', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Export analytics report
   * @param {object} params - Query parameters (start_time, end_time, format, zone)
   */
  async exportAnalytics(params = {}) {
    try {
      const response = await apiClient.get('/api/user/dns/analytics/export', {
        params,
        responseType: params.format === 'csv' ? 'blob' : 'json'
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }
  // ==========================================
  // DNS Authorization
  // ==========================================

  async getAuthRecords(domain, app) {
    try {
      const response = await apiClient.get('/api/user/dns/authorize/records', {
        params: { domain, app }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 我的 DNS 套餐（含绑定状态）。params: { unbound?: 1, include_pending?: 1 }
   */
  async getDnsSubscriptions(params = {}) {
    try {
      const path = params.unbound ? '/api/user/dns/subscriptions/unbound' : '/api/user/dns/subscriptions';
      const { unbound, ...rest } = params;
      const response = await apiClient.get(path, { params: rest });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message, data: [] };
    }
  }


  // One-click DNS (shared by mail / CDN / hosting). params: { app, id, aid?, live? }
  async getOneClickPlan(params) {
    try {
      const response = await apiClient.get('/api/user/dns/oneclick', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // body: { app, id, aid?, keys?, replace?: string[] }
  async applyOneClick(body) {
    try {
      const response = await apiClient.post('/api/user/dns/oneclick/apply', body);
      return response.data;
    } catch (error) {
      const d = error.response?.data || {};
      return { success: false, error: d.error || error.message, status: error.response?.status, quota: d.quota, needed: d.needed };
    }
  }

  async addAuthRecords(domain, app) {
    try {
      const response = await apiClient.post('/api/user/dns/authorize/add', { domain, app });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }
}

export default new DNSService();
